/**
 * Tiempo real del cliente — un socket a Azure Web PubSub por pestaña.
 *
 * Idea: el servidor NO manda datos por el socket, manda un aviso mínimo
 * ({ type }). Cada pantalla decide si ese aviso le incumbe y, si sí, vuelve a
 * pedir sus datos por HTTP. Menos formato que versionar, y nada llega por el
 * socket que el usuario no pudiera pedir igual.
 *
 * Degrada solo: si el backend responde `url: null` (no hay Web PubSub
 * provisionado) esto no abre nada y las pantallas siguen cargando al entrar,
 * exactamente como antes.
 *
 * Se usa el WebSocket nativo del navegador con el subprotocolo `json.webpubsub.azure.v1`
 * en vez del SDK: son ~30 líneas y evita meter una dependencia al bundle.
 */
import { api } from './api';

export type HubEvent = { type: 'followup' | 'patient' | 'config' | 'inbound'; patientId?: string; asesora?: string | null; at?: number };
type Listener = (e: HubEvent) => void;

const listeners = new Set<Listener>();
let socket: WebSocket | null = null;
let starting = false;
let retries = 0;
let disabled = false; // el servidor dijo que no hay tiempo real → no insistir

/** Backoff con techo: si el servicio anda mal no queremos una pestaña abierta
 *  martillando el negotiate cada segundo. */
const backoffMs = () => Math.min(30_000, 1000 * 2 ** Math.min(retries, 5));

async function connect(): Promise<void> {
  if (disabled || starting || socket) return;
  starting = true;
  try {
    const { url } = await api<{ url: string | null }>('realtime/negotiate');
    if (!url) { disabled = true; return; }

    const ws = new WebSocket(url, 'json.webpubsub.azure.v1');
    socket = ws;

    ws.onopen = () => { retries = 0; };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data as string);
        // Los avisos de grupo llegan como { type: 'message', from: 'group', data: {...} }.
        // El resto (system/ack) no nos interesa.
        if (msg?.type === 'message' && msg?.data?.type) {
          for (const fn of listeners) { try { fn(msg.data as HubEvent); } catch { /* un listener roto no tumba a los demás */ } }
        }
      } catch { /* mensaje que no es JSON: ignorar */ }
    };
    const reopen = () => {
      socket = null;
      if (disabled || listeners.size === 0) return;
      retries++;
      setTimeout(() => { void connect(); }, backoffMs());
    };
    ws.onclose = reopen;
    ws.onerror = () => { try { ws.close(); } catch { /* ya estaba cerrado */ } };
  } catch {
    socket = null;
    retries++;
    if (listeners.size > 0) setTimeout(() => { void connect(); }, backoffMs());
  } finally {
    starting = false;
  }
}

/** Suscribe una pantalla a los avisos. Devuelve la función para desuscribirse. */
export function onHubEvent(fn: Listener): () => void {
  listeners.add(fn);
  void connect();
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0 && socket) {
      const s = socket;
      socket = null;
      try { s.close(); } catch { /* nada que hacer */ }
    }
  };
}
